// Propulse Snakes & Ladders — premium learning edition.
// Player (yellow) vs AI bot (red). Roll 1-6, advance. Land on:
//   - Ladder bottom → answer correctly to climb
//   - Snake head → answer correctly to escape; else slide down
//   - Knowledge square → quick question for +15 pts (no penalty)
//   - Boss square (50, 75) → 3-question rapid round for big bonus
//   - Plain square → nothing happens
// First to 100 wins. Overshoot → bounce back from 100.
//
// Architecture mirrors Ludo's premium UI: bokeh background, hopping pin animation,
// confetti bursts on ladder/snake/boss events, announcement banners, fullscreen toggle.

import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, RefreshCw, User, Bot, Volume2, VolumeX, MessageSquareText, Maximize2, Minimize2, Trophy, ArrowUp, Star } from 'lucide-react';

import {
    BOARD_SIZE, TOTAL_CELLS, LADDERS, SNAKES, squareType, botAnswersCorrectly,
    TOPIC_PRESETS, FALLBACK_QUESTIONS, LEARNING_TIPS, COLOR_THEMES,
} from './snake_ladder/snakeLadderEngine';
import SnakeLadderBoard from './snake_ladder/SnakeLadderBoard';
import SLDice from './snake_ladder/SLDice';
import { SLQuestionModal, SLBossModal, SLWinModal, SLTopicPicker } from './snake_ladder/SLModals';
import { ConfettiBurst, Fireworks } from './ludo/LudoConfetti';

export default function SnakeLadderLearning({ game, onPlayed }) {
    // Setup
    const [topicPickerOpen, setTopicPickerOpen] = useState(true);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState('');
    const [topic, setTopic] = useState('');
    const [questionBank, setQuestionBank] = useState(FALLBACK_QUESTIONS);

    // State
    const [players, setPlayers] = useState({ yellow: 0, red: 0 });
    const playersRef = useRef(players);
    useEffect(() => { playersRef.current = players; }, [players]);

    const [turn, setTurn] = useState('yellow');
    const turnRef = useRef('yellow');
    useEffect(() => { turnRef.current = turn; }, [turn]);

    const [dice, setDice] = useState(null);
    const [rolling, setRolling] = useState(false);
    const [done, setDone] = useState(null);
    const doneRef = useRef(null);
    useEffect(() => { doneRef.current = done; }, [done]);

    // Scoring
    const [score, setScore] = useState(0);
    const [climbed, setClimbed] = useState(0);
    const [bitten, setBitten] = useState(0);
    const [learnedCount, setLearnedCount] = useState(0);
    const [log, setLog] = useState([]);
    const [badgeUnlocked, setBadgeUnlocked] = useState(false);

    // Modals
    const [questionModal, setQuestionModal] = useState(null);
    const [bossModal, setBossModal] = useState(null);
    const [winOpen, setWinOpen] = useState(false);

    // FX
    const [muted, setMuted] = useState(false);
    const [announce, setAnnounce] = useState(null);
    const [bursts, setBursts] = useState([]);
    const [lastJump, setLastJump] = useState(null);
    const burstIdRef = useRef(0);

    // Fullscreen
    const wrapperRef = useRef(null);
    const [isFs, setIsFs] = useState(false);
    useEffect(() => {
        const onChange = () => setIsFs(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const toggleFs = async () => {
        const el = wrapperRef.current; if (!el) return;
        try {
            if (!document.fullscreenElement) {
                if (el.requestFullscreen) await el.requestFullscreen();
            } else {
                if (document.exitFullscreen) await document.exitFullscreen();
            }
        } catch { /* iOS Safari blocks */ }
    };

    const pushLog = (entry) => setLog((l) => [{ ...entry, t: Date.now() + Math.random() }, ...l].slice(0, 14));

    const announce_ = (text, tone = 'gold') => {
        const id = Date.now() + Math.random();
        setAnnounce({ text, tone, id });
        setTimeout(() => setAnnounce((a) => (a && a.id === id ? null : a)), 1500);
    };

    const triggerBurst = (kind = 'capture') => {
        burstIdRef.current += 1;
        const id = burstIdRef.current;
        const cfg = kind === 'win' ? { count: 60, scale: 1.4 } : kind === 'ladder' ? { count: 35, scale: 1.1 } : { count: 25, scale: 1.0 };
        setBursts((b) => [...b, { id, ...cfg }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1600);
    };

    const playSound = (kind) => {
        if (muted) return;
        try {
            const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return;
            const ctx = new AC();
            const o = ctx.createOscillator(); const g = ctx.createGain();
            o.type = kind === 'win' ? 'triangle' : kind === 'snake' ? 'sawtooth' : 'sine';
            const map = { roll: 280, move: 540, correct: 880, wrong: 180, snake: 130, ladder: 1100, win: 1320 };
            o.frequency.value = map[kind] || 440;
            o.connect(g); g.connect(ctx.destination);
            g.gain.setValueAtTime(0.0001, ctx.currentTime);
            g.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
            o.start(); o.stop(ctx.currentTime + 0.36);
        } catch { /* ignore */ }
    };

    // === Topic / AI Question Generation (instant start + 24h cache + background swap) ===
    const startWithCurated = () => {
        setQuestionBank(FALLBACK_QUESTIONS);
        setTopic('Curated cables & wires fundamentals');
        setTopicPickerOpen(false);
        pushLog({ kind: 'system', text: '📚 Curated cables & wires bank ready · Game on!' });
    };

    const startWithTopic = async (chosenTopic) => {
        // Start IMMEDIATELY with fallback bank — no waiting.
        setQuestionBank(FALLBACK_QUESTIONS);
        setTopic(chosenTopic);
        setTopicPickerOpen(false);
        pushLog({ kind: 'system', text: `📚 ${chosenTopic} · curated bank loaded — AI brewing in background…` });

        // Try cache first
        try {
            const { data } = await api.get(`/games/snake_ladder/cached-questions`, { params: { topic: chosenTopic } });
            if (data?.questions?.length >= 6) {
                setQuestionBank(data.questions);
                pushLog({ kind: 'system', text: `✨ AI-tuned ${data.questions.length} questions live (cached)` });
                return;
            }
        } catch { /* no cache yet */ }

        // Background AI generation — silently swap in when ready
        setAiLoading(true);
        try {
            const { data } = await api.post('/games/snake-ladder/generate-questions', { topic: chosenTopic, num_questions: 16 });
            const qs = data?.questions || [];
            if (qs.length >= 6) {
                setQuestionBank(qs);
                pushLog({ kind: 'system', text: `✨ AI-tuned ${qs.length} questions now live!` });
            }
        } catch (e) {
            // Silent — game continues with curated bank
            console.warn('Background AI gen failed', e);
            setAiError('AI is slow — sticking with the curated bank.');
        } finally {
            setAiLoading(false);
        }
    };

    // === Dice & Rolling ===
    const rollDice = () => {
        if (rolling || turn !== 'yellow' || done || questionModal || bossModal) return;
        setRolling(true); playSound('roll');
        let n = 0;
        const it = setInterval(() => {
            setDice(1 + Math.floor(Math.random() * 6));
            n += 1;
            if (n >= 10) {
                clearInterval(it);
                const final = 1 + Math.floor(Math.random() * 6);
                setDice(final); setRolling(false);
                onRolled('yellow', final);
            }
        }, 60);
    };

    const botRoll = () => {
        if (doneRef.current) return;
        setRolling(true); playSound('roll');
        let n = 0;
        const it = setInterval(() => {
            setDice(1 + Math.floor(Math.random() * 6));
            n += 1;
            if (n >= 8) {
                clearInterval(it);
                const final = 1 + Math.floor(Math.random() * 6);
                setDice(final); setRolling(false);
                setTimeout(() => onRolled('red', final), 350);
            }
        }, 60);
    };

    // === Movement (hop cell by cell so we can show the journey) ===
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const HOP_MS = 130;

    const setPos = (color, pos) => {
        const next = { ...playersRef.current, [color]: pos };
        playersRef.current = next; setPlayers(next);
    };

    const onRolled = async (color, value) => {
        pushLog({ kind: color === 'yellow' ? 'player' : 'bot', text: `${COLOR_THEMES[color].name} rolled 🎲 ${value}` });
        const cur = playersRef.current[color];
        let target = cur + value;
        if (target > TOTAL_CELLS) {
            // Bounce back from 100
            target = TOTAL_CELLS - (target - TOTAL_CELLS);
            pushLog({ kind: 'system', text: `↩ ${COLOR_THEMES[color].name} overshoots — bounces to ${target}` });
        }
        // Hop pin
        for (let p = cur + 1; p <= target; p++) {
            setPos(color, p);
            playSound('move');
            await sleep(HOP_MS);
        }
        await sleep(200);

        // Win check
        if (target === TOTAL_CELLS) {
            triggerBurst('win'); playSound('win');
            announce_(color === 'yellow' ? '🏆 100! YOU WIN!' : '💀 BOT WINS', color === 'yellow' ? 'gold' : 'red');
            await sleep(600);
            finalize(color === 'yellow');
            return;
        }

        // Landing logic
        const kind = squareType(target);
        if (color === 'yellow') {
            if (kind === 'ladder')         askLadder(target);
            else if (kind === 'snake')     askSnake(target);
            else if (kind === 'boss')      startBoss(target);
            else if (kind === 'knowledge') askKnowledge(target);
            else                            endTurn();
        } else {
            handleBotLanding(target, kind);
        }
    };

    const askKnowledge = (pos) => {
        const q = pickQuestion();
        setQuestionModal({
            kind: 'knowledge', question: q,
            onAnswer: (correct) => {
                setQuestionModal(null);
                if (correct === true) {
                    playSound('correct'); setScore((s) => s + 15); setLearnedCount((n) => n + 1);
                    announce_('✅ +15 pts', 'green');
                    pushLog({ kind: 'player', text: `⭐ Knowledge square @${pos} — correct! +15 pts` });
                } else {
                    playSound('wrong');
                    pushLog({ kind: 'player', text: `⭐ Knowledge square @${pos} — missed (no penalty)` });
                }
                endTurn();
            },
        });
    };

    const askLadder = (bottom) => {
        const top = LADDERS[bottom];
        const q = pickQuestion();
        setQuestionModal({
            kind: 'ladder', question: q,
            onAnswer: async (correct) => {
                setQuestionModal(null);
                if (correct === true) {
                    playSound('ladder'); triggerBurst('ladder');
                    setClimbed((c) => c + 1); setLearnedCount((n) => n + 1); setScore((s) => s + 20);
                    setLastJump({ id: Date.now(), kind: 'ladder' });
                    announce_(`🪜 CLIMB! ${bottom}→${top}`, 'green');
                    pushLog({ kind: 'player', text: `🪜 Climbed ladder ${bottom}→${top}! +20 pts` });
                    // Animate climb hop
                    await sleep(300);
                    setPos('yellow', top);
                    await sleep(500);
                    if (top === TOTAL_CELLS) { finalize(true); return; }
                } else {
                    playSound('wrong');
                    pushLog({ kind: 'player', text: `🪜 Wrong answer at ladder ${bottom} — stayed put` });
                }
                endTurn();
            },
        });
    };

    const askSnake = (head) => {
        const tail = SNAKES[head];
        const q = pickQuestion();
        setQuestionModal({
            kind: 'snake', question: q,
            onAnswer: async (correct) => {
                setQuestionModal(null);
                if (correct === true) {
                    playSound('correct');
                    setLearnedCount((n) => n + 1); setScore((s) => s + 25);
                    announce_('🛡️ ESCAPED!', 'green');
                    pushLog({ kind: 'player', text: `🛡️ Escaped snake @${head}! +25 pts` });
                } else {
                    playSound('snake'); triggerBurst('snake');
                    setBitten((b) => b + 1);
                    setLastJump({ id: Date.now(), kind: 'snake' });
                    announce_(`🐍 SLIDE! ${head}→${tail}`, 'red');
                    pushLog({ kind: 'player', text: `🐍 Snake bit at ${head} — slid to ${tail}` });
                    await sleep(300);
                    setPos('yellow', tail);
                    await sleep(400);
                }
                endTurn();
            },
        });
    };

    const startBoss = (pos) => {
        const qs = sample(questionBank.length ? questionBank : FALLBACK_QUESTIONS, 3);
        setBossModal({ questions: qs, pos });
    };

    const completeBoss = async (correctCount, total) => {
        const pos = bossModal?.pos || playersRef.current.yellow;
        setBossModal(null);
        if (correctCount === total) {
            playSound('win'); triggerBurst('ladder');
            setScore((s) => s + 75); setLearnedCount((n) => n + total);
            pushLog({ kind: 'player', text: `👑 Boss cleared (${pos})! +75 pts + 5-cell leap` });
            announce_('👑 BOSS CLEARED!', 'gold');
            await sleep(300);
            setPos('yellow', Math.min(TOTAL_CELLS, pos + 5));
            await sleep(400);
            if (playersRef.current.yellow >= TOTAL_CELLS) { finalize(true); return; }
        } else if (correctCount >= 2) {
            setScore((s) => s + 40); setLearnedCount((n) => n + correctCount);
            pushLog({ kind: 'player', text: `👑 Boss partial ${correctCount}/${total} — +40 pts` });
        } else {
            pushLog({ kind: 'player', text: `💀 Boss failed (${correctCount}/${total}) — slide back 5` });
            announce_('💀 BOSS FAIL', 'red');
            await sleep(300);
            setPos('yellow', Math.max(1, pos - 5));
            await sleep(400);
        }
        endTurn();
    };

    // === Bot landing logic ===
    const handleBotLanding = async (pos, kind) => {
        if (kind === 'ladder') {
            const top = LADDERS[pos];
            const correct = botAnswersCorrectly();
            if (correct) {
                playSound('ladder');
                pushLog({ kind: 'bot', text: `🪜 Bot climbed ${pos}→${top}` });
                setLastJump({ id: Date.now(), kind: 'ladder' });
                await sleep(300);
                setPos('red', top);
                await sleep(400);
                if (top === TOTAL_CELLS) { finalize(false); return; }
            } else {
                pushLog({ kind: 'bot', text: `🪜 Bot missed ladder @${pos}` });
            }
        } else if (kind === 'snake') {
            const tail = SNAKES[pos];
            const correct = botAnswersCorrectly();
            if (correct) {
                pushLog({ kind: 'bot', text: `🛡️ Bot escaped snake @${pos}` });
            } else {
                playSound('snake');
                pushLog({ kind: 'bot', text: `🐍 Bot bitten @${pos} → ${tail}` });
                setLastJump({ id: Date.now(), kind: 'snake' });
                await sleep(300);
                setPos('red', tail);
                await sleep(400);
            }
        } else if (kind === 'boss') {
            const win = Math.random() < 0.55;
            if (win) {
                pushLog({ kind: 'bot', text: `👑 Bot cleared boss @${pos}` });
                await sleep(300);
                setPos('red', Math.min(TOTAL_CELLS, pos + 5));
                await sleep(300);
            } else {
                pushLog({ kind: 'bot', text: `💀 Bot failed boss @${pos}` });
                await sleep(300);
                setPos('red', Math.max(1, pos - 5));
                await sleep(300);
            }
            if (playersRef.current.red >= TOTAL_CELLS) { finalize(false); return; }
        } else if (kind === 'knowledge') {
            const correct = botAnswersCorrectly();
            pushLog({ kind: 'bot', text: correct ? `⭐ Bot answered knowledge @${pos}` : `⭐ Bot missed knowledge @${pos}` });
        }
        endTurn();
    };

    const endTurn = () => {
        if (doneRef.current) return;
        const next = turnRef.current === 'yellow' ? 'red' : 'yellow';
        turnRef.current = next; setTurn(next);
        if (next === 'red') setTimeout(botRoll, 700);
    };

    // === Finalize ===
    const finalize = async (playerWon) => {
        setDone(playerWon ? 'win' : 'lose');
        const finalScore = playerWon ? score + 100 : score;
        if (playerWon) setScore(finalScore);
        const awarded = Math.max(40, Math.min(400, Math.round(finalScore * (playerWon ? 1.0 : 0.4))));
        try {
            await api.post(`/games/${game.code}/submit`, {
                score: Math.min(finalScore, 100),
                duration_seconds: 0,
                awarded_points: awarded,
                details: { won: playerWon, topic, score: finalScore, climbed, bitten, learned: learnedCount },
            });
            try {
                const { data: me } = await api.get('/auth/me');
                if (me?.badges?.some((b) => b.code === 'ladder_climber')) setBadgeUnlocked(true);
            } catch { /* ignore */ }
        } catch (e) {
            console.warn('Submit failed', e);
        }
        setTimeout(() => setWinOpen(true), 700);
        onPlayed && onPlayed();
    };

    const reset = () => {
        playersRef.current = { yellow: 0, red: 0 };
        setPlayers({ yellow: 0, red: 0 });
        turnRef.current = 'yellow'; setTurn('yellow');
        doneRef.current = null; setDone(null);
        setDice(null); setRolling(false);
        setScore(0); setClimbed(0); setBitten(0); setLearnedCount(0);
        setLog([{ kind: 'system', text: '🎲 New game — pick a topic to begin!', t: Date.now() }]);
        setBadgeUnlocked(false); setWinOpen(false);
        setQuestionModal(null); setBossModal(null);
        setTopicPickerOpen(true); setLastJump(null);
    };

    const closeWinAndExit = () => { setWinOpen(false); onPlayed && onPlayed(); };

    const pickQuestion = () => {
        const bank = questionBank.length ? questionBank : FALLBACK_QUESTIONS;
        return bank[Math.floor(Math.random() * bank.length)];
    };

    // Action prompt for mobile
    let prompt = null; let promptTone = 'info';
    if (!topicPickerOpen && !questionModal && !bossModal && !done) {
        if (rolling) {
            prompt = turn === 'yellow' ? '🎲 Rolling…' : '🤖 Bot rolling…'; promptTone = 'info';
        } else if (turn === 'yellow') {
            prompt = '🎲 Your turn — tap "Roll Dice".'; promptTone = 'action';
        } else {
            prompt = '🤖 Bot is thinking…'; promptTone = 'info';
        }
    }

    return (
        <div ref={wrapperRef} className={`relative overflow-x-hidden ${isFs ? 'bg-slate-950 min-h-screen p-3 sm:p-6 overflow-y-auto' : ''}`} data-testid="snake-ladder-game">
            <SLTopicPicker open={topicPickerOpen} presets={TOPIC_PRESETS} onPick={startWithTopic} onSkip={startWithCurated} loading={aiLoading} errorText={aiError} />

            <div className="grid lg:grid-cols-12 gap-4 lg:gap-5">
                <div className="lg:col-span-8">
                    <div className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-black rounded-3xl p-3 sm:p-5 shadow-2xl ring-1 ring-white/10 overflow-hidden">
                        <AmbientBokeh />
                        <div className="relative z-10">
                            <BoardHeader turn={turn} topic={topic} muted={muted} onToggleMute={() => setMuted((m) => !m)} done={done} isFs={isFs} onToggleFs={toggleFs} aiLoading={aiLoading} />
                            {prompt && (
                                <motion.div
                                    key={prompt}
                                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                    className={`mt-3 px-3 py-2 sm:px-4 sm:py-2.5 rounded-2xl ring-1 backdrop-blur-sm flex items-center gap-2 ${
                                        promptTone === 'action'
                                            ? 'bg-gradient-to-r from-brand-yellow to-amber-400 text-slate-900 ring-amber-300'
                                            : 'bg-white/10 text-white ring-white/10'
                                    }`}
                                    data-testid="sl-action-prompt"
                                >
                                    <div className="text-xs sm:text-sm font-display font-extrabold truncate flex-1">{prompt}</div>
                                </motion.div>
                            )}

                            <div className="mt-3">
                                <SnakeLadderBoard players={players} lastJump={lastJump} />
                            </div>

                            {bursts.map((b) => <ConfettiBurst key={b.id} count={b.count} scale={b.scale} />)}
                            <AnnouncementBanner announce={announce} />

                            <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                                <SLDice value={dice} rolling={rolling} onRoll={rollDice}
                                    disabled={turn !== 'yellow' || done || topicPickerOpen || !!questionModal || !!bossModal}
                                    color={turn === 'yellow' ? 'yellow' : 'red'}
                                    showPointer={turn === 'yellow' && !done && !topicPickerOpen && !questionModal && !bossModal && !rolling} />
                                <Scorecard score={score} climbed={climbed} bitten={bitten} learned={learnedCount} pos={players.yellow} />
                            </div>

                            <Legend />
                        </div>
                    </div>
                </div>

                <aside className="lg:col-span-4 flex flex-col gap-3">
                    {['yellow', 'red'].map((c) => (
                        <PlayerCard key={c} color={c} pos={players[c]} active={turn === c} isPlayer={c === 'yellow'} />
                    ))}
                    <MatchLog entries={log} onReset={done ? reset : null} />
                </aside>
            </div>

            <SLQuestionModal open={!!questionModal} kind={questionModal?.kind} question={questionModal?.question}
                onAnswer={questionModal?.onAnswer || (() => { })} />
            <SLBossModal open={!!bossModal} questions={bossModal?.questions || []} onComplete={completeBoss} />
            {winOpen && done === 'win' && <Fireworks duration={4.5} density={1.6} />}
            <SLWinModal open={winOpen} won={done === 'win'} score={score} climbed={climbed} bitten={bitten} learnedCount={learnedCount}
                badgeUnlocked={badgeUnlocked} onClose={closeWinAndExit} onPlayAgain={reset} />
        </div>
    );
}

// === Sub-components ===
function AnnouncementBanner({ announce }) {
    if (!announce) return null;
    const tones = {
        gold:  { from: '#FFF200', to: '#E0A800', text: '#231F20', shadow: '0 0 30px rgba(255,242,0,0.8)' },
        red:   { from: '#E31E24', to: '#7F1010', text: '#FFFFFF', shadow: '0 0 30px rgba(227,30,36,0.8)' },
        green: { from: '#10B981', to: '#065F46', text: '#FFFFFF', shadow: '0 0 30px rgba(16,185,129,0.8)' },
    };
    const t = tones[announce.tone] || tones.gold;
    return (
        <AnimatePresence>
            <motion.div key={announce.id}
                initial={{ y: -80, scale: 0.5, opacity: 0, rotate: -8 }}
                animate={{ y: 0, scale: [0.5, 1.25, 1], opacity: 1, rotate: [-8, 4, 0] }}
                exit={{ y: -40, scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut', times: [0, 0.6, 1] }}
                className="absolute top-1/4 left-1/2 -translate-x-1/2 pointer-events-none z-40"
                data-testid="sl-announce">
                <div
                    className="px-6 py-3 rounded-2xl font-display font-extrabold text-2xl sm:text-3xl tracking-wide whitespace-nowrap"
                    style={{
                        background: `linear-gradient(180deg, ${t.from} 0%, ${t.to} 100%)`,
                        color: t.text,
                        boxShadow: t.shadow + ', 0 8px 24px rgba(0,0,0,0.6), inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -3px 0 rgba(0,0,0,0.25)',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        border: '2px solid rgba(0,0,0,0.5)',
                    }}>
                    {announce.text}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

function AmbientBokeh() {
    const orbs = [
        { color: '#FFF200', x: '10%', y: '20%', size: 220, delay: 0 },
        { color: '#10b981', x: '78%', y: '18%', size: 200, delay: 1.5 },
        { color: '#E31E24', x: '15%', y: '78%', size: 220, delay: 3 },
        { color: '#a855f7', x: '82%', y: '76%', size: 220, delay: 4.5 },
    ];
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            {orbs.map((o, i) => (
                <motion.div key={i}
                    initial={{ opacity: 0.25, scale: 0.8 }}
                    animate={{ opacity: [0.18, 0.32, 0.18], scale: [0.85, 1.05, 0.85], x: [0, 30, -20, 0], y: [0, -20, 25, 0] }}
                    transition={{ duration: 9 + i * 1.2, repeat: Infinity, ease: 'easeInOut', delay: o.delay }}
                    className="absolute rounded-full"
                    style={{
                        left: o.x, top: o.y, width: o.size, height: o.size, transform: 'translate(-50%, -50%)',
                        background: `radial-gradient(circle at center, ${o.color}66 0%, ${o.color}11 50%, transparent 75%)`,
                        filter: 'blur(28px)',
                    }} />
            ))}
        </div>
    );
}

function BoardHeader({ turn, topic, muted, onToggleMute, done, isFs, onToggleFs, aiLoading }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center"><ArrowUp className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-300">Propulse Cables & Wires</div>
                <div className="font-display font-extrabold text-lg sm:text-2xl text-white truncate">Snakes & Ladders</div>
                <div className="text-[11px] sm:text-xs text-slate-400 truncate flex items-center gap-2">
                    {topic ? <>📚 {topic}</> : '⏳ Pick a topic'}
                    {aiLoading && <span className="text-amber-300 inline-flex items-center gap-1"><Star className="h-3 w-3 animate-pulse" /> AI brewing…</span>}
                </div>
            </div>
            <div className="text-right hidden sm:block">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Turn</div>
                <motion.div animate={{ scale: done ? 1 : [1, 1.07, 1] }} transition={{ repeat: done ? 0 : Infinity, duration: 1.6 }}
                    className={`font-display font-extrabold text-base ${turn === 'yellow' ? 'text-brand-yellow' : 'text-brand-red'}`}
                    data-testid="sl-turn">
                    {turn === 'yellow' ? '👤 You' : '🤖 Bot'}
                </motion.div>
            </div>
            <button onClick={onToggleFs} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="sl-fs-btn">
                {isFs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button onClick={onToggleMute} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="sl-mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
        </div>
    );
}

function Scorecard({ score, climbed, bitten, learned, pos }) {
    return (
        <div className="flex-1 grid grid-cols-5 gap-2 text-center">
            <ScoreCell label="Pos" value={pos} accent="from-amber-400 to-yellow-500" />
            <ScoreCell label="Pts" value={score} accent="from-sky-400 to-blue-500" />
            <ScoreCell label="Climb" value={climbed} accent="from-emerald-400 to-emerald-600" />
            <ScoreCell label="Bites" value={bitten} accent="from-rose-400 to-rose-600" />
            <ScoreCell label="Learn" value={learned} accent="from-violet-400 to-violet-600" />
        </div>
    );
}
function ScoreCell({ label, value, accent }) {
    return (
        <div className={`rounded-2xl bg-gradient-to-br ${accent} p-2 sm:p-2.5 text-slate-900 shadow`}>
            <div className="text-[9px] uppercase tracking-wide font-bold">{label}</div>
            <div className="font-display font-extrabold text-base sm:text-lg leading-none">{value}</div>
        </div>
    );
}
function Legend() {
    return (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] sm:text-[11px] text-slate-300">
            <Tag color="emerald" text="🪜 Ladder · climb on correct" />
            <Tag color="rose" text="🐍 Snake · escape on correct" />
            <Tag color="violet" text="👑 Boss · 3-question round" />
            <Tag color="sky" text="⭐ Knowledge · +15 pts" />
        </div>
    );
}
function Tag({ text }) {
    return <div className="flex items-center gap-1.5 bg-white/5 rounded-xl px-2 py-1.5 border border-white/10"><span>{text}</span></div>;
}

const PLAYER_GRADIENTS = {
    yellow: 'from-brand-yellow to-amber-400 text-slate-900',
    red:    'from-brand-red to-rose-700 text-white',
};
const PLAYER_AURA = {
    yellow: '0 0 24px 6px rgba(255,242,0,0.55)',
    red:    '0 0 24px 6px rgba(227,30,36,0.55)',
};

function PlayerCard({ color, pos, active, isPlayer }) {
    return (
        <motion.div
            animate={active ? { boxShadow: [PLAYER_AURA[color].replace('0.55', '0.4'), PLAYER_AURA[color], PLAYER_AURA[color].replace('0.55', '0.4')], scale: [1, 1.015, 1] } : { boxShadow: '0 4px 12px rgba(0,0,0,0.2)', scale: 1 }}
            transition={active ? { repeat: Infinity, duration: 1.6, ease: 'easeInOut' } : { duration: 0.3 }}
            className={`relative rounded-3xl p-3 sm:p-4 bg-gradient-to-br ${PLAYER_GRADIENTS[color]} ${active ? 'ring-4 ring-white/50' : 'opacity-95'}`}
            data-testid={`sl-player-${color}`}>
            <div className="flex items-center gap-2">
                {isPlayer ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                <div className="font-display font-extrabold text-base sm:text-lg">{COLOR_THEMES[color].name}</div>
                {active && (
                    <motion.span animate={{ opacity: [0.6, 1, 0.6] }} transition={{ repeat: Infinity, duration: 1.2 }}
                        className="ml-auto text-[10px] font-bold uppercase tracking-wide bg-black/25 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" /> Turn
                    </motion.span>
                )}
            </div>
            <div className="mt-2">
                <div className="text-[9px] uppercase font-bold opacity-80">Position</div>
                <div className="font-display font-extrabold text-2xl sm:text-3xl leading-none">{pos} <span className="text-base opacity-60">/100</span></div>
                <div className="mt-2 h-1.5 rounded-full bg-black/20 overflow-hidden">
                    <motion.div animate={{ width: `${pos}%` }} className="h-full bg-white/70" />
                </div>
            </div>
        </motion.div>
    );
}

function MatchLog({ entries, onReset }) {
    return (
        <div className="bg-white rounded-3xl border border-slate-200 p-4 flex-1 min-h-[12rem]">
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-bold text-slate-900 inline-flex items-center gap-2 text-sm"><MessageSquareText className="h-4 w-4 text-slate-500" />Match Log</h3>
                {onReset && (
                    <button onClick={onReset} className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="sl-reset-btn">
                        <RefreshCw className="h-3 w-3" /> Replay
                    </button>
                )}
            </div>
            <div className="space-y-1.5 max-h-[16rem] overflow-y-auto pr-1" data-testid="sl-log">
                <AnimatePresence initial={false}>
                    {entries.map((e) => (
                        <motion.div key={e.t} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                            className={`text-[11px] sm:text-xs p-2 rounded-xl ${e.kind === 'player' ? 'bg-brand-yellow/20 text-slate-900' : e.kind === 'bot' ? 'bg-rose-50 text-rose-900' : 'bg-sky-50 text-sky-900'}`}>
                            {e.text}
                        </motion.div>
                    ))}
                    {entries.length === 0 && <div className="text-[11px] text-slate-400 italic">Roll to begin…</div>}
                </AnimatePresence>
            </div>
        </div>
    );
}

function sample(arr, n) {
    const out = []; const a = arr.slice();
    while (out.length < n && a.length) {
        const i = Math.floor(Math.random() * a.length);
        out.push(a.splice(i, 1)[0]);
    }
    return out;
}
